import { setLanguage as setLng } from '../features/i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { RouteListener } from './components/RouteListner/RouteListener';
import { AffiliateCodeExtractor } from '../Routes/components/AffiliateCodeExtractor';
import Modals from './components/Modals/Modals';
import { ScrollToAnchor } from './components/ScrollToAnchor';
import { AutoRedeemFromDeeplink } from './components/AutoRedeemFromDeeplink';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthenticated, selectLanguage } from '../store/app/selectors';
import { LoadingView } from '../components/LoadingView';
import { LoginFromRefreshToken } from './components/LoginFromRefreshToken';
import { LazyComponents } from './components/LazyComponents';
import { AppRoutes } from '../Routes/App/AppRoutes';
import { useBeforeFirstRender } from '../hooks/useBeforeFirstRender';
import {
  captureEvent,
  DataKeys,
  EventNames,
  sessionStart,
} from '../features/analytics';
import {
  EXPLORE_WEB_AuthAsAnonymous as Data,
  EXPLORE_WEB_AuthAsAnonymousVariables as Variables,
} from '../Routes/Auth/login/__generated__/EXPLORE_WEB_AuthAsAnonymous';
import { getStoredAffiliateCode } from '../features/affiliateCodes/getStoredAffiliateCode';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { UNKNOWN_ERROR } from '../consts';
import { toast } from 'react-toastify';
import { captureInSentry } from './config/reporting/captureInSentry';
import { getUserIDFromJWT } from '../utils/getUserIDFromJWT';
import { removeStoredAffiliateCode } from '../features/affiliateCodes/removeStoredAffiliateCode';
import { loginSuccess } from '../store/app/actions';
import { ROUTES } from '../Routes/routes';
import { AUTH_AS_ANONYMOUS_MUTATION } from '../Routes/Auth/login/ContinueAsGuest';
import { useHistory } from 'react-router';
import { env } from './config/env';
import qs from 'query-string';
import { AutoDisplayRedeemPopup } from './components/AutoDisplayRedeemPopup';
import { CodeKeys } from '../types';

let _variables: Variables | null = null;

const getVariables = () => _variables;

const setVariables = (variables: Variables) => {
  _variables = variables;
};

export const App = () => {
  // call this before app renders
  useBeforeFirstRender(() => {
    // create a session ID and send it to backend. also store the session ID for analytic events.
    sessionStart();
  });

  // commented out because this was causing a flash with react-snap bebcause of the LoadingView.
  // moved this logic to a saga, and we are making sure the language is set in the index.tsx before rendering the app.
  const language = useSelector(selectLanguage);
  const { ready } = useTranslation();
  const [appMounted, setAppMounted] = useState<boolean>(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const isAuthed = useSelector(selectIsAuthenticated);
  const parsedQs = qs.parse(history.location.search);

  // only used for AK - move to a hook
  const [authAsAnonymous] = useMutation<Data, Variables>(
    AUTH_AS_ANONYMOUS_MUTATION,
    {
      onError: (error: Error) => {
        Sentry.withScope((scope) => {
          scope.setExtra('variables', getVariables());
          Sentry.captureException(error);
        });

        const errorMessage = error?.message || UNKNOWN_ERROR;

        toast.error(errorMessage);
      },
      onCompleted: ({ result }) => {
        if (!result) {
          captureInSentry(
            'App.tsx AUTH_AS_ANONYMOUS_MUTATION result is null or undefined',
            { variables: getVariables() }
          );

          return;
        }

        const { jwt, error } = result;

        const userID = jwt ? getUserIDFromJWT(jwt) : null;

        if (jwt && userID) {
          removeStoredAffiliateCode();

          Sentry.configureScope(function (scope) {
            scope.setUser({ id: userID });
          });

          dispatch(loginSuccess({ userId: userID, jwt }));

          captureEvent({
            name: EventNames.USER_REGISTERED,
            data: [{ key: DataKeys.SIGNUP_METHOD, value: 'Anonymous' }],
          });

          // if the user was going to buy before logging in, take him to the shopping cart
          history.replace(
            `${ROUTES.myTrips}?${CodeKeys.REDEEM_CODE}=${
              parsedQs[CodeKeys.REDEEM_CODE]
            }`
          );
        } else {
          const errorMessage = error?.message || UNKNOWN_ERROR;

          captureInSentry(errorMessage, { variables: getVariables() });

          toast.error(errorMessage);
        }
      },
    }
  );

  // only used for AK - move to the same hook
  const authenticateAsAnonymous = () => {
    const variables: Variables = {
      input: {},
    };
    const affiliateCode = getStoredAffiliateCode();

    if (affiliateCode) {
      variables.input.affiliateCode = affiliateCode;
    }

    setVariables(variables);

    authAsAnonymous({ variables });
  };

  useEffect(() => {
    setLng(language);
  }, [language]);

  useEffect(() => {
    setTimeout(() => {
      setAppMounted(true);
    }, 3000);
  }, []);

  // only used for AK - move to the same hook
  useEffect(
    () => {
      if (env.IS_AK && !isAuthed && parsedQs[CodeKeys.REDEEM_CODE]) {
        authenticateAsAnonymous();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!ready) {
    return <LoadingView />;
  }

  return (
    <>
      {/* App routes!  */}
      <AppRoutes />

      {/* put any effect that want to be fired on Route changes. DO NOT REMOVE. Read notes inside. */}
      <Route component={RouteListener} />

      {/* React router doesn't support anchor tags out of the box.
      So we have to use our own component. */}
      <Route component={ScrollToAnchor} />

      {/* extract the affiliate code from the query string and store it in localstorage to be sent with a signup payload */}
      <AffiliateCodeExtractor />

      {/* extract the redeem code from the query string and call the activate dialog */}
      <AutoRedeemFromDeeplink />

      {/* display the activate dialog automatically via url but not fill a redeem code */}
      <AutoDisplayRedeemPopup />

      {/* extract the discount code and store it in localstorage */}
      {/* commented out because we don't use it anymore here; the user enters
      a coupon code manually on the checkout page */}
      {/* <CouponCodeExtractor /> */}

      {/* Modals container WIP */}
      <Modals />

      {/* Log in to a user using a refresh token.
      We currently use them with links in purchase confirmation emails of guest logins.
      https://lithodomosvr.atlassian.net/wiki/spaces/LITHODOMOS/pages/1482850428/Anonymous+guest+logins#If-you-have-made-a-purchase
      */}
      <LoginFromRefreshToken />

      {appMounted ? (
        //  processes that would only work after a page loads
        <LazyComponents />
      ) : null}
    </>
  );
};
